import { AnyProps } from '@/types/vuex/state';
import { Getters } from '@/types/vuex/getters';
import { areSame, isEmpty } from '@/util/ArrayHelper';
import { COMPARING_ITEMS_AMOUNT } from '@/store/index';

export const getters: Getters = {
    isLastColumnHidden (state) {
        return state.items.length <= COMPARING_ITEMS_AMOUNT - 1;
    },
    notEmptyComparingItems (state) {
        return state.tickedItems.filter(id => !!id);
    },
    notEmptyVisibleComparingItems (state, getters) {
        return getters.getVisibleComparingItems.filter(id => !!id);
    },
    comparingItemsAndProps: (state, getters) => (values) => {
        const visibleItems = getters.notEmptyVisibleComparingItems;
        if (visibleItems.length <= 1) {
            const data = { comparingItems: null, props: null };
            const singleItemIndex = state.tickedItems.findIndex(id => id === visibleItems[0]);
            if (visibleItems.length && singleItemIndex !== -1 && !isEmpty(values)) {
                data.comparingItems = getters.notEmptyComparingItems;
                data.props = [values[singleItemIndex]];
            }
            return data;
        }
        let comparingItems, valuesToCheck;
        if (state.isDisplayingDifference && state.isMobile) {
            comparingItems = [state.tickedItems[0], state.tickedItems[1]];
            valuesToCheck = [values[0], values[1]];
        } else {
            comparingItems = state.tickedItems;
            valuesToCheck = values;
        }
        return { comparingItems: comparingItems, props: valuesToCheck };
    },
    valuesOf: (state, getters) => (values) => {
        const { comparingItems, props } = getters.comparingItemsAndProps(values);
        if (!props || isEmpty(props) || (state.isDisplayingDifference && areSame(comparingItems, props))) {
            return null;
        }
        return values;
    },
    valuesOfIgnoreShowDiff: (state, getters) => (values) => {
        const { props } = getters.comparingItemsAndProps(values);
        return !props || isEmpty(props) ? null : values;
    },
    visibleValuesOf: (state, getters) => (values) => {
        return getters.valuesOf(
            values.map((value, index) => index < getters.getComparingListLength ? value : '') as AnyProps
        );
    },
    insurances (state, getters) {
        const get = getters.valuesOf;
        const insurances = get(state.insurances);
        if (insurances) {
            return insurances;
        }
        return !state.isDisplayingDifference && state.insurancesLinks.some(link => !!link)
            ? state.insurances
            : null;
    },
    isEmptyValues: (state, getters) => (valuesList) => {
        if (getters.notEmptyComparingItems.length <= 1) {
            return true;
        }
        return !valuesList.some(values => !isEmpty(getters.getVisibleValues(values)));
    },
    sellerRatings (state, getters) {
        const reviewLinks = getters.visibleValuesOf(state.dealerReviewLinks);
        return reviewLinks && isEmpty(reviewLinks) ? null : getters.visibleValuesOf(state.dealerRatings);
    },
    isItemComparing: (state, getters) => (itemId: number) => {
        if (state.isMobile) {
            const items = getters.notEmptyComparingItems;
            return [items[0], items[1]].includes(+itemId);
        }
        return state.tickedItems.includes(+itemId);
    },
    itemsAmount (state, getters) {
        const notEmptyItemsLen = getters.notEmptyComparingItems.length;
        if (!state.isMobile) {
            return notEmptyItemsLen;
        }
        return notEmptyItemsLen === COMPARING_ITEMS_AMOUNT ? notEmptyItemsLen - 1 : notEmptyItemsLen;
    },
    isComparingListFull: (state, getters) => getters.itemsAmount === getters.getComparingListLength,
    getComparingListLength: (state) => state.isMobile ? COMPARING_ITEMS_AMOUNT - 1 : COMPARING_ITEMS_AMOUNT,
    getVisibleComparingItems: (state, getters) => state.tickedItems.slice(0, getters.getComparingListLength),
    getVisibleValues: (state, getters) => (values) => {
        const endIndex = state.isDisplayingDifference && areSame(getters.getVisibleComparingItems, values)
            ? 0
            : getters.getComparingListLength;
        return values.slice(0, endIndex);
    },
    getVisibleTickedItems: (state, getters) => state.tickedItems
        .map((value, index) => index < getters.getComparingListLength ? value : 0),
    getIncludedOptions: state => state.includedOptionsCache[state.tickedItems[state.includedOptionsModal.index]] || '',
    listItems: state => {
        const itemsInList = Object.keys(state.listCache).map(id => +id);
        return state.items.filter(id => itemsInList.includes(id));
    },
    allListItemsLoaded: (state, getters) => getters.listItems.length >= state.items.length
};
